/**
 * The content organiser (spec 002): the current folder's subfolders, scenes
 * and stories. The URL (`/` or `/folder/:folderId`) is the current folder.
 *
 * Every card behaves the same: a click selects (Shift/Cmd/Ctrl extends),
 * double-click or Enter opens, F2 or the pencil renames. Cards (or a
 * selection) drag onto a folder card, a breadcrumb segment or "Go up".
 * Deletes go through the shared ConfirmDeleteModal / ConfirmFolderDeleteModal
 * (spec 003). There is no scene-level Publish here.
 */
import { useState, useEffect, useRef, KeyboardEvent, DragEvent, ReactNode } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Form, Dropdown, Breadcrumb, Alert, Modal } from 'react-bootstrap';
import {
  PlusCircle,
  CameraReels,
  Calendar,
  Trash,
  Copy,
  Folder,
  FolderPlus,
  Pencil,
  Plus,
  FileEarmarkPlay,
  Book,
  ArrowUp,
  HouseFill,
  GripVertical,
  Image,
  XCircle,
} from 'react-bootstrap-icons';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import { useApiClient, FolderSummary } from '../api/client';
import { useModalContext } from '../contexts/ModalContext';
import { Folder as FolderType, StoryApiResponse } from '../../../Shared/src/@types';
import MediaPicker from '../modals/MediaPicker';
import StoryStartSheet from '../components/organiser/StoryStartSheet';
import SceneStartSheet from '../components/organiser/SceneStartSheet';
import ConfirmDeleteModal, { ConfirmFolderDeleteModal } from '../components/ConfirmDeleteModal';
import {
  ItemRef,
  itemKey,
  parseItemKey,
  parseFolderParam,
  sortByName,
  uniqueName,
  breadcrumbPath,
  canDropInto,
} from '../components/organiser/organiser';

interface IconMedia {
  id: number;
  name: string;
  type: string;
  objectUrl?: string;
}

interface FolderWithIcon extends FolderType {
  icon?: IconMedia;
}

interface SceneWithIcon {
  id: number;
  name: string;
  userId: number;
  folderId: number | null;
  iconId: number | null;
  createdAt: string;
  updatedAt: string;
  icon?: IconMedia;
}

type PendingDelete =
  | { kind: 'scene'; scene: SceneWithIcon; usedIn: string[] | null }
  | { kind: 'story'; story: StoryApiResponse }
  | { kind: 'folder'; folder: FolderWithIcon; summary: FolderSummary | null };

const DRAG_TYPE = 'application/x-storymotion-content';

const errorOf = (res: { success: boolean; error?: string } | null | undefined, fallback: string) =>
  (res && !res.success && res.error) || fallback;

/** Inline rename: Enter or blur saves, Escape cancels, an empty name is refused with a message. */
function RenameInput({ value, onSave, onCancel }: { value: string; onSave: (name: string) => void; onCancel: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const done = useRef(false);
  const submit = (raw: string) => {
    if (done.current) return;
    const name = raw.trim();
    if (!name) {
      setError('A name is required.');
      return;
    }
    done.current = true;
    if (name === value) onCancel();
    else onSave(name);
  };
  return (
    <div onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}>
      <Form.Control
        type="text"
        size="sm"
        defaultValue={value}
        autoFocus
        aria-label="New name"
        isInvalid={!!error}
        onFocus={(e) => e.currentTarget.select()}
        onChange={() => error && setError(null)}
        onBlur={(e) => submit(e.currentTarget.value)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          e.stopPropagation();
          if (e.key === 'Enter') submit(e.currentTarget.value);
          else if (e.key === 'Escape') {
            done.current = true;
            onCancel();
          }
        }}
      />
      {error && (
        <div className="invalid-feedback d-block" role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

const Home = () => {
  const navigate = useNavigate();
  const { folderId: folderIdParam } = useParams<{ folderId: string }>();
  const location = useLocation();
  const apiClient = useApiClient();
  const { addModal, removeModal } = useModalContext();

  const [scenes, setScenes] = useState<SceneWithIcon[]>([]);
  const [stories, setStories] = useState<StoryApiResponse[]>([]);
  const [folders, setFolders] = useState<FolderWithIcon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<string[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [dragged, setDragged] = useState<ItemRef[] | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const dragImageRef = useRef<HTMLElement | null>(null);

  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [cloneOf, setCloneOf] = useState<SceneWithIcon | null>(null);
  const [cloneBusy, setCloneBusy] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // ─── Current folder: the URL is the source of truth ──────────────────────

  const parsedFolder = parseFolderParam(folderIdParam);
  const folderNotFound =
    parsedFolder === 'invalid' || (parsedFolder !== null && !loading && !folders.some((f) => f.id === parsedFolder));
  const currentFolderId: number | null = parsedFolder === 'invalid' || folderNotFound ? null : parsedFolder;
  const currentFolder = folders.find((f) => f.id === currentFolderId) ?? null;

  // A new folder in the URL starts with nothing selected or being renamed.
  useEffect(() => {
    setSelected([]);
    setEditingKey(null);
  }, [folderIdParam]);

  const goToFolder = (folderId: number | null) => navigate(folderId === null ? '/' : `/folder/${folderId}`);

  // ─── Loading ─────────────────────────────────────────────────────────────

  // Object URLs for folder and scene icons. Each list's URLs are revoked when
  // that list is replaced and on unmount (spec 001, StrictMode-idempotent).
  const folderIconUrls = useRef<string[]>([]);
  const sceneIconUrls = useRef<string[]>([]);

  const replaceIconUrls = (ref: { current: string[] }, next: string[]) => {
    ref.current.forEach((url) => URL.revokeObjectURL(url));
    ref.current = next;
  };

  /**
   * Fetch the icon media for `iconIds`. With `signal` aborted part-way, every
   * URL already made is revoked and null is returned (the caller drops it).
   */
  const loadIcons = async (iconIds: number[], signal?: AbortSignal) => {
    const results = await Promise.all(
      iconIds.map(async (id: number) => {
        try {
          const mediaResult = await apiClient.getMediaById(id, { signal });
          if (mediaResult.success) {
            const blob = await apiClient.getMediaFileBlob(id, { signal });
            return { ...mediaResult.data, objectUrl: URL.createObjectURL(blob) };
          }
        } catch (err) {
          if (!signal?.aborted) console.error(`Failed to load media ${id}:`, err);
        }
        return null;
      })
    );
    const urls = results.flatMap((icon) => (icon?.objectUrl ? [icon.objectUrl as string] : []));
    if (signal?.aborted) {
      urls.forEach((url) => URL.revokeObjectURL(url));
      return null;
    }
    const mediaMap: { [key: number]: any } = {};
    results.forEach((icon, index) => {
      if (icon) mediaMap[iconIds[index]] = icon;
    });
    return { mediaMap, urls };
  };

  const iconIdsOf = (rows: any[]): number[] =>
    rows.map((row: any) => row.iconId).filter((id: any) => id != null && typeof id === 'number');

  const fetchFolders = async (signal?: AbortSignal) => {
    try {
      const response = await apiClient.getFolders({ signal });
      if (signal?.aborted || !response.success) return;
      const foldersData = response.data;
      const icons = await loadIcons(iconIdsOf(foldersData), signal);
      if (!icons) return;

      replaceIconUrls(folderIconUrls, icons.urls);
      setFolders(
        foldersData.map((folder: any) => ({
          ...folder,
          icon: folder.iconId != null && typeof folder.iconId === 'number' ? icons.mediaMap[folder.iconId] : undefined,
        }))
      );
    } catch (err) {
      if (!signal?.aborted) console.error('Failed to fetch folders:', err);
    }
  };

  const fetchScenes = async (signal?: AbortSignal) => {
    try {
      const response = await apiClient.getScenes({ signal });
      if (signal?.aborted || !response.success) return;
      const scenesData = response.data;
      const icons = await loadIcons(iconIdsOf(scenesData), signal);
      if (!icons) return;

      replaceIconUrls(sceneIconUrls, icons.urls);
      setScenes(
        scenesData.map((scene: any) => ({
          ...scene,
          icon: scene.iconId != null && typeof scene.iconId === 'number' ? icons.mediaMap[scene.iconId] : undefined,
        }))
      );
    } catch (err) {
      if (!signal?.aborted) console.error('Failed to fetch scenes:', err);
    }
  };

  const fetchStories = async (signal?: AbortSignal) => {
    try {
      const response = await apiClient.getStories({ signal });
      if (signal?.aborted) return;
      if (response.success) {
        setStories(response.data);
      }
    } catch (err) {
      if (!signal?.aborted) console.error('Failed to fetch stories:', err);
    }
  };

  const refreshAll = () => Promise.all([fetchFolders(), fetchScenes(), fetchStories()]);

  // Initial load. Under StrictMode the first run is aborted and its results
  // (and any icon URLs it made) are discarded.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    Promise.all([fetchScenes(signal), fetchFolders(signal), fetchStories(signal)]).then(() => {
      if (!signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [apiClient]);

  // Revoke every icon URL still held when Home unmounts.
  useEffect(
    () => () => {
      replaceIconUrls(folderIconUrls, []);
      replaceIconUrls(sceneIconUrls, []);
    },
    []
  );

  // ─── What is shown ───────────────────────────────────────────────────────

  const visibleFolders = sortByName(folders.filter((f) => (f.parentId ?? null) === currentFolderId));
  const visibleScenes = sortByName(scenes.filter((s) => (s.folderId ?? null) === currentFolderId));
  const visibleStories = sortByName(stories.filter((s) => (s.folderId ?? null) === currentFolderId));
  const isEmpty = visibleFolders.length === 0 && visibleScenes.length === 0 && visibleStories.length === 0;

  const nameOf = (ref: ItemRef): string =>
    (ref.kind === 'folder'
      ? folders.find((f) => f.id === ref.id)?.name
      : ref.kind === 'scene'
        ? scenes.find((s) => s.id === ref.id)?.name
        : stories.find((s) => s.id === ref.id)?.name) ?? '';

  // ─── Select / open / rename ──────────────────────────────────────────────

  const select = (ref: ItemRef, extend: boolean) => {
    const key = itemKey(ref);
    setSelected((prev) => (extend ? (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]) : [key]));
  };

  const open = (ref: ItemRef) => {
    if (ref.kind === 'folder') goToFolder(ref.id);
    else navigate(`/${ref.kind}/${ref.id}`, { state: { from: location.pathname } });
  };

  const startRename = (ref: ItemRef) => {
    setSelected([itemKey(ref)]);
    setEditingKey(itemKey(ref));
  };

  const rename = async (ref: ItemRef, name: string) => {
    setEditingKey(null);
    const old = nameOf(ref);
    try {
      const res =
        ref.kind === 'folder'
          ? await apiClient.updateFolder(ref.id, { name })
          : ref.kind === 'scene'
            ? await apiClient.updateScene(ref.id, { name })
            : await apiClient.updateStory(ref.id, { name });
      if (!res.success) throw new Error(errorOf(res, 'Rename failed'));
      if (ref.kind === 'folder') setFolders((prev) => prev.map((f) => (f.id === ref.id ? { ...f, name } : f)));
      else if (ref.kind === 'scene') setScenes((prev) => prev.map((s) => (s.id === ref.id ? { ...s, name } : s)));
      else setStories((prev) => prev.map((s) => (s.id === ref.id ? { ...s, name } : s)));
    } catch (e) {
      setError(`Could not rename "${old}": ${e instanceof Error ? e.message : 'Rename failed'}`);
    }
  };

  const cardProps = (ref: ItemRef, label: string) => {
    const key = itemKey(ref);
    const isSelected = selected.includes(key);
    return {
      role: 'option',
      tabIndex: 0,
      'aria-selected': isSelected,
      'aria-label': label,
      'data-testid': `${ref.kind}-card`,
      onClick: (e: React.MouseEvent) => select(ref, e.shiftKey || e.metaKey || e.ctrlKey),
      onDoubleClick: () => editingKey !== key && open(ref),
      onKeyDown: (e: KeyboardEvent) => {
        if (e.target !== e.currentTarget || editingKey === key) return;
        if (e.key === 'Enter') {
          e.preventDefault();
          open(ref);
        } else if (e.key === 'F2') {
          e.preventDefault();
          startRename(ref);
        }
      },
      draggable: editingKey !== key,
      onDragStart: (e: DragEvent) => startDrag(e, ref),
      onDragEnd: endDrag,
    };
  };

  // ─── Create ──────────────────────────────────────────────────────────────

  const createFolder = async () => {
    try {
      const name = uniqueName('New Folder', visibleFolders.map((f) => f.name));
      const res = await apiClient.createFolder({ name, parentId: currentFolderId });
      if (!res.success) throw new Error(errorOf(res, 'Create failed'));
      setFolders((prev) => [...prev, res.data]);
      startRename({ kind: 'folder', id: res.data.id });
    } catch (e) {
      setError(`Could not create a folder: ${e instanceof Error ? e.message : 'Create failed'}`);
    }
  };

  // New scene opens the scene start sheet (spec 029); the sheet creates the scene.
  const [sceneSheetOpen, setSceneSheetOpen] = useState(false);
  const createScene = () => setSceneSheetOpen(true);

  // New story opens the start sheet (spec 024 D-12); the sheet creates the story.
  const [startSheetOpen, setStartSheetOpen] = useState(false);
  const createStory = () => setStartSheetOpen(true);

  // ─── Move (drag onto a folder card, a breadcrumb segment or "Go up") ─────

  const startDrag = (e: DragEvent, ref: ItemRef) => {
    const key = itemKey(ref);
    const items = selected.includes(key) && selected.length > 1 ? selected.map(parseItemKey) : [ref];
    setDragged(items);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(DRAG_TYPE, JSON.stringify(items));

    if (items.length > 1) {
      const badge = document.createElement('div');
      badge.textContent = `${items.length} items`;
      badge.style.cssText =
        'position:absolute;top:-1000px;left:-1000px;padding:10px 14px;border-radius:8px;background:#0d6efd;color:#fff;font-weight:600;font-size:14px;';
      document.body.appendChild(badge);
      dragImageRef.current = badge;
      e.dataTransfer.setDragImage(badge, 20, 20);
    }
  };

  const endDrag = () => {
    setDragged(null);
    setDropTarget(null);
    if (dragImageRef.current) {
      dragImageRef.current.remove();
      dragImageRef.current = null;
    }
  };

  const draggedFrom = (e: DragEvent): ItemRef[] | null => {
    if (dragged) return dragged;
    try {
      const raw = e.dataTransfer.getData(DRAG_TYPE);
      return raw ? (JSON.parse(raw) as ItemRef[]) : null;
    } catch {
      return null;
    }
  };

  const dropProps = (target: number | null, key: string) => ({
    onDragOver: (e: DragEvent) => {
      const items = dragged;
      if (!items) return;
      const ok = canDropInto(folders, items, target);
      if (ok) e.preventDefault(); // only an accepted target lets the drop happen
      e.stopPropagation();
      e.dataTransfer.dropEffect = ok ? 'move' : 'none';
      setDropTarget(ok ? key : null);
    },
    onDragLeave: () => setDropTarget((prev) => (prev === key ? null : prev)),
    onDrop: (e: DragEvent) => {
      const items = draggedFrom(e);
      if (!items) return;
      e.preventDefault();
      e.stopPropagation();
      endDrag();
      if (canDropInto(folders, items, target)) moveTo(items, target);
    },
  });

  const parentOf = (ref: ItemRef): number | null =>
    ref.kind === 'folder'
      ? (folders.find((f) => f.id === ref.id)?.parentId ?? null)
      : ref.kind === 'scene'
        ? (scenes.find((s) => s.id === ref.id)?.folderId ?? null)
        : (stories.find((s) => s.id === ref.id)?.folderId ?? null);

  const moveTo = async (items: ItemRef[], target: number | null) => {
    const moving = items.filter((item) => parentOf(item) !== target);
    const results = await Promise.all(
      moving.map(async (item) => {
        try {
          const res =
            item.kind === 'folder'
              ? await apiClient.updateFolder(item.id, { parentId: target })
              : item.kind === 'scene'
                ? await apiClient.updateScene(item.id, { folderId: target })
                : await apiClient.updateStory(item.id, { folderId: target });
          return { item, ok: res.success, error: errorOf(res, 'Move failed') };
        } catch (e) {
          return { item, ok: false, error: e instanceof Error ? e.message : 'Move failed' };
        }
      })
    );
    const movedKeys = new Set(results.filter((r) => r.ok).map((r) => itemKey(r.item)));
    setFolders((prev) => prev.map((f) => (movedKeys.has(`folder-${f.id}`) ? { ...f, parentId: target } : f)));
    setScenes((prev) => prev.map((s) => (movedKeys.has(`scene-${s.id}`) ? { ...s, folderId: target } : s)));
    setStories((prev) => prev.map((s) => (movedKeys.has(`story-${s.id}`) ? { ...s, folderId: target } : s)));
    setSelected([]);
    const failed = results.filter((r) => !r.ok);
    if (failed.length > 0) {
      setError(`Could not move "${nameOf(failed[0].item)}": ${failed[0].error}`);
    }
  };

  // ─── Folder icon ─────────────────────────────────────────────────────────

  const setFolderIcon = async (folderId: number, iconId: number | null) => {
    const res = await apiClient.updateFolder(folderId, { iconId }).catch(() => null);
    if (!res?.success) setError(errorOf(res, 'Could not change the folder icon'));
    await fetchFolders();
  };

  const pickFolderIcon = (folderId: number) => {
    addModal({
      title: 'Set folder icon',
      body: (
        <MediaPicker
          typeFilter="image"
          lockFilter={true}
          onSelect={async (mediaId) => {
            removeModal();
            await setFolderIcon(folderId, mediaId);
          }}
          onCancel={() => removeModal()}
        />
      ),
      size: 'xl',
    });
  };

  // ─── Clone ───────────────────────────────────────────────────────────────

  const clone = async (scene: SceneWithIcon, cloneAssets: boolean) => {
    setCloneBusy(true);
    setCloneError(null);
    try {
      const res = await apiClient.cloneScene(scene.id, { cloneAssets });
      if (!res.success) throw new Error(errorOf(res, 'Clone failed'));
      await fetchScenes();
      setSelected([`scene-${res.data.id}`]);
      setCloneOf(null);
    } catch (e) {
      setCloneError(e instanceof Error ? e.message : 'Clone failed');
    } finally {
      setCloneBusy(false);
    }
  };

  // ─── Delete ──────────────────────────────────────────────────────────────

  const askDeleteScene = async (scene: SceneWithIcon) => {
    setPendingDelete({ kind: 'scene', scene, usedIn: null });
    const res = await apiClient.getSceneStoryLinks(scene.id).catch(() => null);
    const names = res?.success ? Array.from(new Set(res.data.links.map((l) => l.storyName))) : [];
    setPendingDelete((prev) => (prev?.kind === 'scene' && prev.scene.id === scene.id ? { ...prev, usedIn: names } : prev));
  };

  const askDeleteFolder = async (folder: FolderWithIcon) => {
    setPendingDelete({ kind: 'folder', folder, summary: null });
    const res = await apiClient.getFolderSummary(folder.id).catch(() => null);
    if (!res?.success) {
      setPendingDelete(null);
      setError(`Could not check what is inside "${folder.name}": ${errorOf(res, 'the server did not answer')}`);
      return;
    }
    setPendingDelete((prev) =>
      prev?.kind === 'folder' && prev.folder.id === folder.id ? { ...prev, summary: res.data } : prev
    );
  };

  /** Runs a delete; a refusal throws so the modal shows it and stays open. */
  const runDelete = async (call: () => Promise<{ success: boolean; error?: string }>) => {
    const res = await call();
    if (!res.success) throw new Error(errorOf(res, 'Delete failed'));
    setSelected([]);
    await refreshAll();
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Layout subtitle="Loading your content..." sidebar={<Sidebar />}>
        <div className="d-flex justify-content-center align-items-center h-100">
          <Spinner animation="border" />
        </div>
      </Layout>
    );
  }

  if (folderNotFound) {
    return (
      <Layout subtitle="Manage your content" sidebar={<Sidebar />}>
        <Container fluid className="py-5 text-center" data-testid="folder-not-found">
          <Folder size={64} className="text-muted mb-3" />
          <h5>Folder not found</h5>
          <p className="text-muted">It may have been deleted, or the link is wrong.</p>
          <Link to="/" className="btn btn-primary">
            Go to Home
          </Link>
        </Container>
      </Layout>
    );
  }

  const path = breadcrumbPath(folders, currentFolderId);

  const createActions = (variant: 'menu' | 'buttons') => {
    const actions: { key: string; label: string; icon: ReactNode; run: () => void }[] = [
      { key: 'folder', label: 'New folder', icon: <FolderPlus size={20} className="text-success" />, run: createFolder },
      { key: 'scene', label: 'New scene', icon: <CameraReels size={20} className="text-primary" />, run: createScene },
      { key: 'story', label: 'New story', icon: <Book size={20} className="text-info" />, run: createStory },
    ];
    if (variant === 'menu') {
      return actions.map((a) => (
        <Dropdown.Item key={a.key} className="d-flex align-items-center gap-2 py-2" onClick={a.run}>
          {a.icon}
          <span>{a.label}</span>
        </Dropdown.Item>
      ));
    }
    return (
      <div className="d-flex justify-content-center gap-2 flex-wrap">
        {actions.map((a) => (
          <Button key={a.key} variant="outline-primary" className="d-flex align-items-center gap-2" onClick={a.run}>
            {a.icon}
            {a.label}
          </Button>
        ))}
      </div>
    );
  };

  const cardClass = (ref: ItemRef, extra = '') => {
    const key = itemKey(ref);
    const isDragged = dragged?.some((d) => itemKey(d) === key);
    return [
      'h-100 shadow-sm organiser-card',
      selected.includes(key) ? 'border-primary border-2 selected' : '',
      dropTarget === key ? 'border-primary border-2 bg-light' : '',
      isDragged ? 'opacity-50' : '',
      extra,
    ].join(' ');
  };

  /** Card buttons must not select, open or start a drag. */
  const action = (label: string, onClick: () => void, children: ReactNode, variant = 'outline-secondary', disabled = false) => (
    <Button
      variant={variant}
      size="sm"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      {children}
    </Button>
  );

  const title = (ref: ItemRef, name: string) =>
    editingKey === itemKey(ref) ? (
      <RenameInput value={name} onSave={(n) => rename(ref, n)} onCancel={() => setEditingKey(null)} />
    ) : (
      <Card.Title className="h6 mb-2 fw-bold text-truncate" title={name}>
        {name || <span className="text-muted fst-italic">Unnamed</span>}
      </Card.Title>
    );

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

  return (
    <Layout subtitle="Manage your content" sidebar={<Sidebar />}>
      <Container fluid className="py-4">
        <div className="mb-4">
          <Dropdown>
            <Dropdown.Toggle
              variant="primary"
              size="lg"
              className="d-flex align-items-center gap-2 shadow-sm"
              style={{ borderRadius: '24px', padding: '10px 24px' }}
            >
              <Plus size={24} />
              Create
            </Dropdown.Toggle>
            <Dropdown.Menu className="shadow" style={{ minWidth: '200px' }}>
              {createActions('menu')}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} data-testid="organiser-error">
            {error}
          </Alert>
        )}

        <Breadcrumb className="mb-3" data-testid="breadcrumb">
          <Breadcrumb.Item
            active={currentFolderId === null}
            linkAs="span"
            onClick={currentFolderId === null ? undefined : () => goToFolder(null)}
            style={currentFolderId === null ? undefined : { cursor: 'pointer' }}
            className={dropTarget === 'crumb-root' ? 'bg-light rounded px-1' : ''}
            {...dropProps(null, 'crumb-root')}
          >
            <HouseFill className="me-1" />
            Home
          </Breadcrumb.Item>
          {path.map((folder, index) => {
            const last = index === path.length - 1;
            const key = `crumb-${folder.id}`;
            return (
              <Breadcrumb.Item
                key={folder.id}
                active={last}
                linkAs="span"
                onClick={last ? undefined : () => goToFolder(folder.id)}
                style={last ? undefined : { cursor: 'pointer' }}
                className={dropTarget === key ? 'bg-light rounded px-1' : ''}
                {...(last ? {} : dropProps(folder.id, key))}
              >
                <span className="d-inline-flex align-items-center gap-1">
                  {folder.icon ? (
                    <img
                      src={folder.icon.objectUrl}
                      alt=""
                      style={{ width: '16px', height: '16px', objectFit: 'cover', borderRadius: '2px' }}
                    />
                  ) : (
                    <Folder size={14} className="text-success" />
                  )}
                  {folder.name}
                </span>
              </Breadcrumb.Item>
            );
          })}
        </Breadcrumb>

        <Row
          className="g-4"
          role="listbox"
          aria-label="Folders, scenes and stories"
          aria-multiselectable
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelected([]);
          }}
        >
          {currentFolderId !== null && (
            <Col xs={12} sm={6} md={4} xl={3}>
              <Card
                className={`h-100 shadow-sm organiser-card ${dropTarget === 'go-up' ? 'border-primary border-2 bg-light' : ''}`}
                style={{ minHeight: '200px', cursor: 'pointer' }}
                data-testid="go-up"
                aria-label="Go up"
                role="button"
                tabIndex={0}
                onClick={() => goToFolder(currentFolder?.parentId ?? null)}
                onKeyDown={(e) => e.key === 'Enter' && goToFolder(currentFolder?.parentId ?? null)}
                {...dropProps(currentFolder?.parentId ?? null, 'go-up')}
              >
                <Card.Body className="d-flex flex-column align-items-center justify-content-center text-center">
                  <ArrowUp size={64} className="text-secondary mb-3" />
                  <Card.Title className="h6 mb-2 fw-bold text-muted">Go up</Card.Title>
                </Card.Body>
              </Card>
            </Col>
          )}

          {visibleFolders.map((folder) => {
            const ref: ItemRef = { kind: 'folder', id: folder.id };
            return (
              <Col key={itemKey(ref)} xs={12} sm={6} md={4} xl={3}>
                <Card
                  className={cardClass(ref)}
                  style={{ minHeight: '200px', cursor: 'pointer' }}
                  {...cardProps(ref, `Folder ${folder.name}`)}
                  {...dropProps(folder.id, itemKey(ref))}
                >
                  <Card.Body className="d-flex flex-column align-items-center text-center position-relative">
                    <div className="position-absolute top-0 start-0 p-2 grab-handle" style={{ cursor: 'grab' }}>
                      <GripVertical size={16} className="text-muted" />
                    </div>
                    <div className="mb-3 position-relative d-inline-block">
                      <Folder size={64} className="text-success" />
                      {folder.icon && (
                        <img
                          src={folder.icon.objectUrl}
                          alt={folder.icon.name}
                          style={{
                            width: '28px',
                            height: '28px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -35%)',
                          }}
                        />
                      )}
                    </div>
                    <div className="flex-grow-1 w-100">
                      {title(ref, folder.name)}
                      <Card.Subtitle className="text-muted small mb-2">
                        <Calendar size={12} className="me-1" />
                        {formatDate(folder.createdAt)}
                      </Card.Subtitle>
                    </div>
                    <div className="mt-auto d-flex gap-2 justify-content-center w-100">
                      {action('Rename', () => startRename(ref), <Pencil size={14} />)}
                      {action('Set icon', () => pickFolderIcon(folder.id), <Image size={14} />)}
                      {folder.icon && action('Remove icon', () => setFolderIcon(folder.id, null), <XCircle size={14} />)}
                      {action('Delete', () => askDeleteFolder(folder), <Trash size={14} />, 'outline-danger')}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}

          {visibleScenes.map((scene) => {
            const ref: ItemRef = { kind: 'scene', id: scene.id };
            return (
              <Col key={itemKey(ref)} xs={12} sm={6} md={4} xl={3}>
                <Card className={cardClass(ref)} style={{ minHeight: '200px', cursor: 'pointer' }} {...cardProps(ref, `Scene ${scene.name}`)}>
                  <Card.Body className="d-flex flex-column align-items-center text-center position-relative">
                    <div className="position-absolute top-0 start-0 p-2 grab-handle" style={{ cursor: 'grab' }}>
                      <GripVertical size={16} className="text-muted" />
                    </div>
                    <div className="mb-3">
                      {scene.icon ? (
                        <img
                          src={scene.icon.objectUrl}
                          alt={scene.icon.name}
                          style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '8px' }}
                        />
                      ) : (
                        <CameraReels size={64} className="text-primary" />
                      )}
                    </div>
                    <div className="flex-grow-1 w-100">
                      {title(ref, scene.name)}
                      <Card.Subtitle className="text-muted small mb-2">
                        <Calendar size={12} className="me-1" />
                        {formatDate(scene.createdAt)}
                      </Card.Subtitle>
                    </div>
                    <div className="mt-auto d-flex gap-2 justify-content-center w-100">
                      {action('Rename', () => startRename(ref), <Pencil size={14} />)}
                      {action(
                        'Clone',
                        () => {
                          setCloneError(null);
                          setCloneOf(scene);
                        },
                        <Copy size={14} />
                      )}
                      {action('Delete', () => askDeleteScene(scene), <Trash size={14} />, 'outline-danger')}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}

          {visibleStories.map((story) => {
            const ref: ItemRef = { kind: 'story', id: story.id };
            const sceneCount = story.storyScenes?.length ?? 0;
            return (
              <Col key={itemKey(ref)} xs={12} sm={6} md={4} xl={3}>
                <Card className={cardClass(ref)} style={{ minHeight: '200px', cursor: 'pointer' }} {...cardProps(ref, `Story ${story.name}`)}>
                  <Card.Body className="d-flex flex-column align-items-center text-center position-relative">
                    <div className="position-absolute top-0 start-0 p-2 grab-handle" style={{ cursor: 'grab' }}>
                      <GripVertical size={16} className="text-muted" />
                    </div>
                    <div className="mb-3">
                      <Book size={64} className="text-info" />
                    </div>
                    <div className="flex-grow-1 w-100">
                      {title(ref, story.name)}
                      <Card.Subtitle className="text-muted small mb-1">
                        <Calendar size={12} className="me-1" />
                        {formatDate(story.createdAt)}
                      </Card.Subtitle>
                      <div className="text-muted small">
                        {sceneCount} scene{sceneCount !== 1 ? 's' : ''}
                      </div>
                    </div>
                    <div className="mt-auto d-flex gap-2 justify-content-center w-100">
                      {action('Open', () => open(ref), <FileEarmarkPlay size={14} />, 'outline-primary')}
                      {action('Rename', () => startRename(ref), <Pencil size={14} />)}
                      {action('Delete', () => setPendingDelete({ kind: 'story', story }), <Trash size={14} />, 'outline-danger')}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>

        {isEmpty && (
          <div className="text-center py-5" data-testid="organiser-empty">
            <PlusCircle size={64} className="text-muted mb-3" />
            <h5 className="text-muted">{currentFolderId !== null ? 'This folder is empty' : 'Nothing here yet'}</h5>
            <p className="text-muted">Create a folder, a scene or a story here.</p>
            {createActions('buttons')}
          </div>
        )}
      </Container>

      {/* Clone */}
      <Modal show={cloneOf !== null} onHide={() => !cloneBusy && setCloneOf(null)} centered data-testid="clone-modal">
        <Modal.Header closeButton={!cloneBusy}>
          <Modal.Title>Clone "{cloneOf?.name}"</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>Clone</strong> makes a copy that shares its images, sounds and voice-overs with the original.
          </p>
          <p className="mb-0">
            <strong>Clone with assets</strong> also copies every media file the scene uses, so the copy is fully
            independent.
          </p>
          {cloneError && (
            <Alert variant="danger" className="mt-2 mb-0">
              {cloneError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={cloneBusy} onClick={() => setCloneOf(null)}>
            Cancel
          </Button>
          <Button variant="secondary" disabled={cloneBusy} onClick={() => cloneOf && clone(cloneOf, false)}>
            Clone
          </Button>
          <Button variant="primary" disabled={cloneBusy} onClick={() => cloneOf && clone(cloneOf, true)}>
            Clone with assets
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Deletes */}
      <ConfirmDeleteModal
        show={pendingDelete?.kind === 'scene'}
        title={pendingDelete?.kind === 'scene' ? `Delete "${pendingDelete.scene.name}"?` : ''}
        checking={pendingDelete?.kind === 'scene' && pendingDelete.usedIn === null}
        usedIn={pendingDelete?.kind === 'scene' ? (pendingDelete.usedIn ?? undefined) : undefined}
        usedInWarning="It will be removed from those stories."
        onClose={() => setPendingDelete(null)}
        actions={
          pendingDelete?.kind === 'scene'
            ? [
                {
                  key: 'delete',
                  label: 'Delete',
                  isDefault: true,
                  run: () => {
                    const id = pendingDelete.scene.id;
                    return runDelete(() => apiClient.deleteScene(id));
                  },
                },
              ]
            : []
        }
      >
        <p className="mb-0">This cannot be undone.</p>
      </ConfirmDeleteModal>

      <ConfirmDeleteModal
        show={pendingDelete?.kind === 'story'}
        title={pendingDelete?.kind === 'story' ? `Delete "${pendingDelete.story.name}"?` : ''}
        onClose={() => setPendingDelete(null)}
        actions={
          pendingDelete?.kind === 'story'
            ? [
                {
                  key: 'orphan',
                  label: 'Delete story, keep its scenes',
                  variant: 'warning',
                  isDefault: true,
                  run: () => {
                    const id = pendingDelete.story.id;
                    return runDelete(() => apiClient.deleteStory(id, 'orphan'));
                  },
                },
                {
                  key: 'delete',
                  label: 'Delete story and its own scenes',
                  variant: 'danger',
                  run: () => {
                    const id = pendingDelete.story.id;
                    return runDelete(() => apiClient.deleteStory(id, 'delete'));
                  },
                },
              ]
            : []
        }
      >
        {pendingDelete?.kind === 'story' && (() => {
          const owned = (pendingDelete.story.storyScenes ?? []).filter((ss) => ss.mode === 'owned').length;
          return (
            <p className="mb-0">
              {owned > 0
                ? `It has ${owned} scene${owned === 1 ? '' : 's'} of its own. "Keep its scenes" puts them in this folder; the other choice deletes them too.`
                : 'It has no scenes of its own; scenes it links to are kept either way.'}
            </p>
          );
        })()}
      </ConfirmDeleteModal>

      <ConfirmFolderDeleteModal
        show={pendingDelete?.kind === 'folder'}
        folderName={pendingDelete?.kind === 'folder' ? pendingDelete.folder.name : ''}
        checking={pendingDelete?.kind === 'folder' && pendingDelete.summary === null}
        contents={(() => {
          const summary = pendingDelete?.kind === 'folder' ? pendingDelete.summary : null;
          return {
            folders: summary?.folders ?? 0,
            items: 0,
            kinds: [
              { count: summary?.scenes ?? 0, one: 'scene', many: 'scenes' },
              { count: summary?.stories ?? 0, one: 'story', many: 'stories' },
            ],
          };
        })()}
        usedIn={pendingDelete?.kind === 'folder' ? pendingDelete.summary?.outsideStories.map((s) => s.name) : undefined}
        usedInWarning={'"Delete everything" also removes its scenes from these stories.'}
        onClose={() => setPendingDelete(null)}
        onMoveContentsUp={() => {
          if (pendingDelete?.kind !== 'folder') return;
          const id = pendingDelete.folder.id;
          return runDelete(() => apiClient.deleteFolder(id, 'moveUp'));
        }}
        onDeleteEverything={() => {
          if (pendingDelete?.kind !== 'folder') return;
          const id = pendingDelete.folder.id;
          return runDelete(() => apiClient.deleteFolder(id, 'deleteAll'));
        }}
      />

      <style
        dangerouslySetInnerHTML={{
          __html: `
          .organiser-card { transition: transform 0.1s; }
          .organiser-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important; }
          .organiser-card:focus-visible { outline: 2px solid #0d6efd; outline-offset: 2px; }
          .organiser-card.selected { box-shadow: 0 0 0 0.2rem rgba(13,110,253,0.25) !important; }
          .grab-handle { opacity: 0.3; transition: opacity 0.2s; }
          .grab-handle:hover { opacity: 1; }
          .organiser-card:hover .grab-handle { opacity: 0.7; }
        `,
        }}
      />
      <SceneStartSheet
        show={sceneSheetOpen}
        onHide={() => setSceneSheetOpen(false)}
        folderId={currentFolderId}
        siblingSceneNames={visibleScenes.map((s) => s.name)}
        from={location.pathname}
      />
      <StoryStartSheet
        show={startSheetOpen}
        onHide={() => setStartSheetOpen(false)}
        folderId={currentFolderId}
        siblingStoryNames={visibleStories.map((s) => s.name)}
        from={location.pathname}
      />
    </Layout>
  );
};

export default Home;
